import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Send, Phone, MapPin, Mail, Clock, CheckCircle, AlertCircle } from 'lucide-react';

export default function ContactForm() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null); // 'loading' | 'success' | 'error'
  const [statusMsg, setStatusMsg] = useState('');

  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2) e.name = 'Name is required (min 2 chars)';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Valid email is required';
    if (!form.phone.trim() || form.phone.trim().length < 7) e.phone = 'Valid phone number is required';
    if (!form.subject.trim() || form.subject.trim().length < 2) e.subject = 'Subject is required';
    if (!form.message.trim() || form.message.trim().length < 10) e.message = 'Message is required (min 10 chars)';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus('loading');

    try {
      const res = await fetch("https://formsubmit.co/ajax/almanzar@fitzgerald-law.com", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          ...form,
          _cc: "chicagorealtynow@gmail.com",
          _subject: `New Legal Inquiry: ${form.subject}`,
          _template: "table",
          _captcha: "false"
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setStatus('success');
        setStatusMsg("Thank you! Your message has been sent successfully.");
        setForm({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        setStatus('error');
        setStatusMsg(data.message || 'Submission failed. Please try again.');
      }
    } catch (err) {
      setStatus('error');
      setStatusMsg('Network error. Please check your connection.');
    }
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 rounded-md font-body text-sm transition-all duration-300 outline-none border ${errors[field]
      ? 'border-red-400 focus:border-red-500'
      : 'focus:border-[var(--color-accent)] focus:shadow-[0_0_15px_var(--color-glow)]'
    }`;

  const inputStyle = {
    background: 'var(--color-bg)',
    borderColor: 'var(--color-border)',
    color: 'var(--color-text)',
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32" style={{ background: 'var(--color-bg)' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase mb-4"
            style={{ background: 'var(--color-glow)', color: 'var(--color-accent)' }}
          >
            Get In Touch
          </span>
          <h2 data-testid="contact-heading" className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mb-4" style={{ color: 'var(--color-text)' }}>
            Let Us Help You With Expert{' '}
            <span className="gradient-text">Legal Solutions</span>
          </h2>
          <p className="max-w-2xl mx-auto font-body text-lg" style={{ color: 'var(--color-text-muted)' }}>
            Get your free evaluation today. Our team is ready to discuss your legal needs.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-2 space-y-8"
          >
            <div className="p-8 rounded-lg border" style={{ background: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
              <h3 className="font-heading font-bold text-xl mb-6 tracking-tight" style={{ color: 'var(--color-text)' }}>
                Contact Information
              </h3>
              <div className="space-y-5">
                {[
                  { icon: Phone, label: '(917) 624-4469', href: 'tel:+19176244469' },
                  { icon: Mail, label: 'Almanzar@fitzgerald-law.com', href: 'mailto:Almanzar@fitzgerald-law.com' },
                  { icon: MapPin, label: '470 Mamaroneck Avenue, White Plains New York', href: null },
                  { icon: Clock, label: 'Mon–Fri: 9AM – 6PM\nWeekends: By Appointment', href: null },
                ].map(({ icon: Icon, label, href }) => (
                  <div key={label} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: 'var(--color-glow)', color: 'var(--color-accent)' }}
                    >
                      <Icon size={16} />
                    </div>
                    {href ? (
                      <a href={href} className="font-body text-sm whitespace-pre-line hover:underline" style={{ color: 'var(--color-text)' }}>
                        {label}
                      </a>
                    ) : (
                      <span className="font-body text-sm whitespace-pre-line" style={{ color: 'var(--color-text-muted)' }}>
                        {label}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="lg:col-span-3"
          >
            <form
              onSubmit={handleSubmit}
              className="p-8 rounded-lg border"
              style={{ background: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
            >
              {status === 'success' && (
                <div className="flex items-center gap-3 p-4 rounded-md mb-6 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                  <CheckCircle size={18} className="text-emerald-500 flex-shrink-0" />
                  <span className="text-emerald-700 dark:text-emerald-300 text-sm font-body">{statusMsg}</span>
                </div>
              )}
              {status === 'error' && (
                <div className="flex items-center gap-3 p-4 rounded-md mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  <AlertCircle size={18} className="text-red-500 flex-shrink-0" />
                  <span className="text-red-700 dark:text-red-300 text-sm font-body">{statusMsg}</span>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block font-body text-xs font-semibold mb-1.5 tracking-wide uppercase" style={{ color: 'var(--color-text-muted)' }}>
                    Full Name *
                  </label>
                  <input
                    id="name"
                    type="text"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    className={inputClass('name')}
                    style={inputStyle}
                    placeholder="John Doe"
                  />
                  {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label htmlFor="email" className="block font-body text-xs font-semibold mb-1.5 tracking-wide uppercase" style={{ color: 'var(--color-text-muted)' }}>
                    Email *
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    className={inputClass('email')}
                    style={inputStyle}
                    placeholder="john@example.com"
                  />
                  {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="phone" className="block font-body text-xs font-semibold mb-1.5 tracking-wide uppercase" style={{ color: 'var(--color-text-muted)' }}>
                    Phone *
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    value={form.phone}
                    onChange={e => setForm({ ...form, phone: e.target.value })}
                    className={inputClass('phone')}
                    style={inputStyle}
                    placeholder="(917) 624-4469"
                  />
                  {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
                </div>
                <div>
                  <label htmlFor="subject" className="block font-body text-xs font-semibold mb-1.5 tracking-wide uppercase" style={{ color: 'var(--color-text-muted)' }}>
                    Subject *
                  </label>
                  <input
                    id="subject"
                    type="text"
                    value={form.subject}
                    onChange={e => setForm({ ...form, subject: e.target.value })}
                    className={inputClass('subject')}
                    style={inputStyle}
                    placeholder="Legal Consultation"
                  />
                  {errors.subject && <p className="text-red-400 text-xs mt-1">{errors.subject}</p>}
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block font-body text-xs font-semibold mb-1.5 tracking-wide uppercase" style={{ color: 'var(--color-text-muted)' }}>
                  Message *
                </label>
                <textarea
                  id="message"
                  rows={5}
                  value={form.message}
                  onChange={e => setForm({ ...form, message: e.target.value })}
                  className={`${inputClass('message')} resize-none`}
                  style={inputStyle}
                  placeholder="Please describe your legal needs..."
                />
                {errors.message && <p className="text-red-400 text-xs mt-1">{errors.message}</p>}
              </div>

              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full flex items-center justify-center gap-2 px-8 py-4 rounded-md text-white font-semibold transition-all duration-300 hover:shadow-[0_0_30px_rgba(6,182,212,0.4)] hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(135deg, var(--color-secondary), var(--color-accent))' }}
              >
                {status === 'loading' ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Send size={16} />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
